import { ExpressionVisitor } from 'app/model/adapters/dataflow/dslExpressions/expression.visitor';
import {
    Expression, BasicExpression, IntegerLiteral, FloatLiteral, StringLiteral,
    Assignment, MetaFunction, FunctionExpression, RegexLiteral, ColumnOperand, FieldSelector, ArrayIndexer,
    ParameterReference, ParameterOperand, MetaAssignment, TransientOperand, BinaryOperator, AddMinus, Datatype, ComplexDatatype, DecimalDatatype, ArrayDatatype, MetaArgument, MapDatatype, ExternalDataType
} from 'app/model/dataflow/expressionTypes';
import {
    DataFlowColumnArgument, IDataFlowArguments,
    DataFlowParameterArgument, IUsedIdentifiers, DataFlowCacheSinkArgument, DataFlowUserDefinedArgument
} from 'app/model/dataflow/dataflow-expression-utilities';
import {
    IDataFlowDataType, BooleanDataFlowType, IntegerDataFlowType,
    FloatDataFlowType, StringDataFlowType, DataFlowDataType, NumberDataFlowType, AnyDataFlowType, NullDataFlowType, ArrayDataFlowType, LongDataFlowType, DecimalDataFlowType, MapDataFlowType
} from 'app/model/dataflow/dataflow-datatype';

import { format } from 'app/shared/utils/string.util';
import { DataFlowExpressionFunctions } from 'app/model/dataflow/dataflow-expression-functions';
import { DataFlowFunctionArgument } from 'app/model/dataflow/dataflow-function-argument';
import { IErrorExpression } from 'app/model/dataflow/IErrorExpression';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { ComplexDataFlowType, ComplexField, ExternalDataFlowType } from 'app/model/dataflow/dataflow-complex-datatype';
import { DataFlowTypesUtils } from 'app/model/dataflow/dataflow-type-utilities';
import { ModelValidatorBase } from './model.validator.base';
import { Constants } from 'app/shared/shared-constants';
import { IMarkerData, MarkerSeverity } from 'app/shared/components/monaco/monaco-models';
import { GlobalVariables } from 'app/services/globals';
import { Application } from 'app/application';
import { UserDefinedFunctionsService } from 'app/services/user-defined-functions.service';

/* eslint-disable @typescript-eslint/no-explicit-any */
export class ModelMarkerVisitor extends ExpressionVisitor {
    public modelMarkers: IMarkerData[] = [];
    public expressionErrors: string[] = [];
    public usedIdentifiers: IUsedIdentifiers = {
        dataFlowColumns: [],
        dataFlowFunctions: [],
        dataFlowParameters: [],
        dataFlowCacheSinks: []
    };

    constructor(
        tokenStream: any,
        private _availableDataFlowArguments: IDataFlowArguments,
        private _validators: ModelValidatorBase[] = []) {
        super(tokenStream);
    }

    public visitStruct(context: any): Expression {
        const expression = super.visitStruct(context);
        const complexReturnType = new ComplexDataFlowType();
        for (const assignment of expression.struct.assignments) {
            const dataFlowExpression = new DataFlowExpression();
            dataFlowExpression.expression = assignment.expression ? assignment.expression.text : '';
            dataFlowExpression.returnType = assignment.expression ? assignment.expression.returnType : new AnyDataFlowType();
            const unescapedName = assignment.columnIdentifier ? Constants.dataFlowEscapedNameValidation.test(assignment.columnIdentifier.text) ?
                                    assignment.columnIdentifier.text.substring(1, assignment.columnIdentifier.text.length - 1) : assignment.columnIdentifier.text : '';
            complexReturnType.fields.push(new ComplexField(unescapedName, dataFlowExpression, complexReturnType));
        }
        expression.returnType = complexReturnType;
        this._validators.forEach((validator) => validator.visitStruct(expression));

        return expression;
    }

    public visitParameterRef(context: any): ParameterReference {
        const parameterRef = super.visitParameterRef(context);
        if (parameterRef.parameterOperand.referencedObject) {
            parameterRef.returnType = parameterRef.parameterOperand.returnType;
            parameterRef.returnValue = parameterRef.parameterOperand.returnValue;
            parameterRef.referencedObject = parameterRef.parameterOperand.referencedObject;
            if (parameterRef.referencedObject instanceof DataFlowParameterArgument) {
                // We can have the same parameter referenced more than once, hence, make a distinction between adding a new parameter and adding a new expression based in the same parameter.
                const usedIdentifier = this.usedIdentifiers.dataFlowParameters.find((param) => param.parameter === parameterRef.referencedObject);
                if (!usedIdentifier) {
                    this.usedIdentifiers.dataFlowParameters.push({
                        expression: [parameterRef],
                        parameter: parameterRef.referencedObject
                    });
                } else {
                    usedIdentifier.expression.push(parameterRef);
                }
            }
            this._validators.forEach((validator) => validator.visitParameterRef(parameterRef));
        } else {
            this._addError(parameterRef.parameterOperand, stringResources.DataFlowEditorErrorParameterNotFound);
        }

        return parameterRef;
    }

    public visitParameterOperand(context: any): ParameterOperand {
        const parameterOperand = super.visitParameterOperand(context);
        if (parameterOperand.basicName) {
            const parameter = this._findParameter(parameterOperand.basicName.text);
            if (parameter) {
                parameterOperand.returnType = DataFlowTypesUtils.createType(parameter.type);
                parameterOperand.returnValue = parameter.name;
                parameterOperand.referencedObject = parameter;
            }
            this._validators.forEach((validator) => validator.visitParameterOperand(parameterOperand));
        }

        return parameterOperand;
    }

    public visitCast(context: any): Expression {
        const expression = super.visitCast(context);
        const inlineDatatype = expression.cast.inlineDatatype || new Datatype('string');
        expression.returnType = this._convertDataType(inlineDatatype);
        return expression;
    }

    public visitColumnOperandRef(context: any): Expression {
        const expression = super.visitColumnOperandRef(context);

        expression.returnType = expression.columnOperand.returnType;
        if (expression.columnOperand.referencedObject) {
            expression.returnValue = expression.columnOperand.returnValue;
            expression.referencedObject = expression.columnOperand.referencedObject;
            if (expression.referencedObject instanceof DataFlowColumnArgument) {
                // We can have the same column referenced more than once, hence, make a distinction between adding a new column and adding a new expression based in the same column.
                const usedIdentifier = this.usedIdentifiers.dataFlowColumns.find((col) => col.column === expression.referencedObject);
                if (!usedIdentifier) {
                    this.usedIdentifiers.dataFlowColumns.push({
                        expression: [expression],
                        column: expression.referencedObject
                    });
                } else {
                    usedIdentifier.expression.push(expression);
                }
            }
            this._validators.forEach((validator) => validator.visitColumnOperandRef(expression));
        } else {
            if (DataFlowTypesUtils.isUserDefinedFunctionArgumentPattern(expression.columnOperand.text)) {
                this._addError(expression.columnOperand, stringResources.UDFArgumentNotFound);
            } else {
                this._addError(expression.columnOperand, stringResources.DataFlowEditorErrorColumnNotFound);
            }
        }
        return expression;
    }

    // Visit a parse tree produced by DataflowParser#columnOperand.
    public visitColumnOperand(context: any): ColumnOperand {
        const columnOperand = super.visitColumnOperand(context);
        const columnName = columnOperand.getFullyQualifiedNameWhenBasicOperand();
        if (columnName) {
            const column = this._findColumn(columnName);
            if (column) {
                columnOperand.returnType = DataFlowTypesUtils.cloneType(column.dataFlowColumn.dataFlowColumnType);
                columnOperand.returnValue = column.name;
                columnOperand.referencedObject = column;
            } else {
                const userDefinedArgument = this._findUserDefinedArgument(columnName);
                if (userDefinedArgument) {
                    columnOperand.returnType = DataFlowTypesUtils.createType(userDefinedArgument.type);
                    columnOperand.returnValue = userDefinedArgument.name;
                    columnOperand.referencedObject = userDefinedArgument;
                }
            }
            this._validators.forEach((validator) => validator.visitColumnOperand(columnOperand));
        }

        return columnOperand;
    }

    public visitTransientRef(context: any): Expression {
        const expression = super.visitTransientRef(context);

        expression.returnType = expression.transientOperand.returnType;
        if (expression.transientOperand.referencedObject) {
            expression.returnValue = expression.transientOperand.returnValue;
            expression.referencedObject = expression.transientOperand.referencedObject;
            if (expression.referencedObject instanceof DataFlowColumnArgument) {
                // We can have the same column referenced more than once, hence, make a distinction between adding a new column and adding a new expression based in the same column.
                const usedIdentifier = this.usedIdentifiers.dataFlowColumns.find((col) => col.column === expression.referencedObject);
                if (!usedIdentifier) {
                    this.usedIdentifiers.dataFlowColumns.push({
                        expression: [expression],
                        column: expression.referencedObject
                    });
                } else {
                    usedIdentifier.expression.push(expression);
                }
            }
            this._validators.forEach((validator) => validator.visitTransientRef(expression));
        } else {
            this._addError(expression.transientOperand, stringResources.DataFlowEditorErrorColumnNotFound);
        }
        return expression;
    }

    public visitTransientOperand(context: any): TransientOperand {
        const transientOperand = super.visitTransientOperand(context);
        const columnName = transientOperand.getFullyQualifiedNameWhenBasicOperand();
        if (columnName) {
            const column = this._findTransientColumn(columnName);
            if (column) {
                transientOperand.returnType = DataFlowTypesUtils.cloneType(column.dataFlowColumn.dataFlowColumnType);
                transientOperand.returnValue = column.name;
                transientOperand.referencedObject = column;
            }
            this._validators.forEach((validator) => validator.visitTransientOperand(transientOperand));
        }

        return transientOperand;
    }

    public visitXor(context: any): Expression {
        const expression = super.visitXor(context);
        this._checkExpressionBooleanType(expression.xor.leftExpression);
        this._checkExpressionBooleanType(expression.xor.rightExpression);
        expression.returnType = new BooleanDataFlowType();
        this._validators.forEach((validator) => validator.visitXor(expression));

        return expression;
    }

    public visitOr(context: any): Expression {
        const expression = super.visitOr(context);
        this._checkExpressionBooleanType(expression.or.leftExpression);
        this._checkExpressionBooleanType(expression.or.rightExpression);
        expression.returnType = new BooleanDataFlowType();
        this._validators.forEach((validator) => validator.visitOr(expression));

        return expression;
    }

    public visitFunctionExpr(context: any): Expression {
        const expression = super.visitFunctionExpr(context);
        let availableFunction;

        if (expression.functionExpression.basicName.identifier) {
            availableFunction = this._findFunction(expression.functionExpression.basicName.identifier.name) || this._findUserDefinedFunction(expression.functionExpression.basicName.identifier.name);
        }
        if (availableFunction) {
            const dataFlowFunction: DataFlowFunctionArgument = availableFunction as DataFlowFunctionArgument;

            this.usedIdentifiers.dataFlowFunctions.push({
                expression: [expression],
                dataFlowFunction
            });

            expression.returnType = dataFlowFunction.evaluateType(expression.functionExpression.children);
            expression.functionExpression.children.forEach((child, index: number) => {
                this._validateColumnType(child as Expression, dataFlowFunction, index);
            });
            this._checkTotalNumberOfArguments(dataFlowFunction, expression.functionExpression);
            const errors = dataFlowFunction.validate(expression, context.parentCtx);
            if (errors) {
                errors.forEach((errorExpression) => this._addError(errorExpression.expression, errorExpression.errorMessage, errorExpression.treatErrorAsWarning));
            }
            this._validators.forEach((validator) => validator.visitFunctionExpr(expression));
        } else {
            this._addError(expression.functionExpression.basicName,
                stringResources.DataFlowEditorErrorUnknownFunction, GlobalVariables.enableDataFlowFunctionValidationBypass /*treatErrorAsWarning */);
        }
        return expression;
    }

    public visitArray(context: any): Expression {
        const expression = super.visitArray(context);
        expression.returnType = new ArrayDataFlowType();
        const arrayItems = expression.array.children;
        if (arrayItems.length) {
            const widestType = DataFlowTypesUtils.getWidestType(arrayItems.filter((item) => !!item).map((item) => item.returnType));
            (expression.returnType as ArrayDataFlowType).arrayType = widestType;
            if (widestType instanceof AnyDataFlowType) {
                this._addError(expression, stringResources.DataFlowEditorErrorArrayNotSameType);
            }
        } else {
            (expression.returnType as ArrayDataFlowType).arrayType = new AnyDataFlowType();
        }
        this._validators.forEach((validator) => validator.visitArray(expression));
        return expression;
    }

    public visitEmptyArray(context: any): Expression {
        const expression = super.visitEmptyArray(context);
        expression.returnType = new ArrayDataFlowType();
        (expression.returnType as ArrayDataFlowType).arrayType = new AnyDataFlowType();
        return expression;
    }

    public visitArrayIndexer(context: any): Expression {
        const expression = super.visitArrayIndexer(context);
        const leftExpressionType = expression.arrayIndexer.leftExpression.returnType;
        if (!leftExpressionType || (!DataFlowTypesUtils.isArrayType(leftExpressionType) && !DataFlowTypesUtils.isMapType(leftExpressionType))) {
            this._addError(expression.arrayIndexer.leftExpression, stringResources.DataFlowEditorErrorArrayIndexerNoArray);
        } else {
            expression.returnType = DataFlowTypesUtils.isMapType(leftExpressionType)
                ? DataFlowTypesUtils.cloneType((leftExpressionType as MapDataFlowType).mapValueType)
                : DataFlowTypesUtils.cloneType((leftExpressionType as ArrayDataFlowType).arrayType);

            if (DataFlowTypesUtils.isMapType(leftExpressionType)) {
                const rightExpressionType = expression.arrayIndexer.rightExpression.returnType;
                if (!DataFlowTypesUtils.parameterTypeMatch(rightExpressionType, (leftExpressionType as MapDataFlowType).mapKeyType.typeString)) {
                    this._addError(expression.arrayIndexer.rightExpression,
                        format(stringResources.DataFlowEditorErrorArrayIndexerTypeDoesNotMatch, rightExpressionType.typeString, (leftExpressionType as MapDataFlowType).mapKeyType.typeString));
                }
            }
        }
        this._validators.forEach((validator) => validator.visitArrayIndexer(expression));
        return expression;
    }

    public visitMap(context: any): Expression {
        const expression = super.visitMap(context);
        let keyType: IDataFlowDataType;
        let valueType: IDataFlowDataType;
        if (expression && expression.map && expression.map.children) {
            if (expression.map.children.length % 2 !== 0 || expression.map.children.some((e) => !e)) {
                this._addError(expression, stringResources.DataFlowEditorMapMissingArguments);
            }
            keyType = expression.map.children[0] ? expression.map.children[0].returnType : new AnyDataFlowType();
            valueType = expression.map.children[1] ? expression.map.children[1].returnType : new AnyDataFlowType();
            for (let i = 2; i < expression.map.children.length; i++) {
                const exp = expression.map.children[i];
                if (!exp || !exp.returnType) {
                    continue;
                }
                if (i % 2 === 0) {
                    keyType = DataFlowTypesUtils.typeMatch(keyType.typeString, exp.returnType.typeString) ? keyType : new AnyDataFlowType();
                } else {
                    valueType = DataFlowTypesUtils.typeMatch(valueType.typeString, exp.returnType.typeString) ? valueType : new AnyDataFlowType();
                }
            }
        }
        expression.returnType = new MapDataFlowType(keyType, valueType);
        return expression;
    }

    public visitFieldSelector(context: any): Expression {
        const expression = super.visitFieldSelector(context);
        let leftExpression = expression.fieldSelector.leftExpression;
        const leftExpressionType = leftExpression.returnType;
        if (!(leftExpressionType instanceof ComplexDataFlowType) && !((leftExpressionType instanceof ArrayDataFlowType))) {
            this._addError(leftExpression, stringResources.DataFlowEditorErrorFieldSelectorNoComplexType);
        } else {
            const leftExpressionActualType = (leftExpressionType instanceof ComplexDataFlowType ? leftExpressionType : leftExpressionType.arrayType) as ComplexDataFlowType;
            if (expression.fieldSelector.rightExpression) {
                const basicName = expression.fieldSelector.fieldName.basicName;
                const fieldName = basicName.identifier ? basicName.identifier.name : basicName.text;
                const existingField = leftExpressionActualType.fields.find((field) => field.entityName === fieldName);
                if (existingField) {
                    expression.returnType = existingField.dataFlowExpression.returnType;
                    // we need to find the root column
                    while (leftExpression.columnOperand instanceof FieldSelector || leftExpression.columnOperand instanceof ArrayIndexer) {
                        leftExpression = leftExpression.columnOperand.leftExpression;
                    }
                    if (leftExpression.referencedObject instanceof DataFlowColumnArgument) {
                        const dataflowColumn = this.usedIdentifiers.dataFlowColumns.find((col) => col.column === leftExpression.referencedObject);
                        if (dataflowColumn) {
                            const newField = {
                                name: `${expression.fieldSelector.leftExpression.text}.${expression.fieldSelector.rightExpression.text}`
                            };
                            dataflowColumn.fields = dataflowColumn.fields || [];
                            const currentFieldIndex = dataflowColumn.fields.findIndex((field) => newField.name.indexOf(field.name) > -1);
                            if (currentFieldIndex > -1) {
                                dataflowColumn.fields[currentFieldIndex] = newField;
                            } else {
                                dataflowColumn.fields.push(newField);
                            }
                        }
                    }
                } else {
                    this._addError(expression.fieldSelector.rightExpression,
                        format(stringResources.DataFlowEditorErrorFieldSelectorNoFieldWithName, fieldName));
                }
            }
        }
        this._validators.forEach((validator) => validator.visitFieldSelector(expression));

        return expression;
    }

    public visitAnd(context: any): Expression {
        const expression = super.visitAnd(context);

        this._checkExpressionBooleanType(expression.and.leftExpression);
        this._checkExpressionBooleanType(expression.and.rightExpression);

        expression.returnType = new BooleanDataFlowType();
        this._validators.forEach((validator) => validator.visitAnd(expression));
        return expression;
    }

    public visitAddMinus(context: any): Expression {
        const expression = super.visitAddMinus(context);
        const leftExpression = expression.addMinus.leftExpression;
        const rightExpression = expression.addMinus.rightExpression;
        if (leftExpression && rightExpression) {
            this._validateAddMinusArgType(leftExpression, rightExpression, expression.addMinus.operation === 'minus');
        }
        expression.returnType = this._getReturnTypeForBinaryOperator(leftExpression, rightExpression, expression.addMinus);
        this._validators.forEach((validator) => validator.visitAddMinus(expression));
        return expression;
    }

    public visitLiteralExpr(context: any): Expression {
        const expression = super.visitLiteralExpr(context);
        if (expression.literal.value instanceof IntegerLiteral) {
            // TODO - ans - How to handle short here.
            expression.returnType = expression.literal.value.text.toLowerCase().endsWith('l') ? new LongDataFlowType() : new IntegerDataFlowType();
        } else if (expression.literal.value instanceof FloatLiteral) {
            // TODO - ans - How to handle double here.
            expression.returnType = new FloatDataFlowType();
        } else if (expression.literal.value instanceof StringLiteral || expression.literal.value instanceof RegexLiteral) {
            expression.returnType = new StringDataFlowType();
        }

        this._validators.forEach((validator) => validator.visitLiteralExpr(expression.literal));

        return expression;
    }

    public visitInterpolation(context: any): Expression {
        const expression = super.visitInterpolation(context);
        expression.returnType = new StringDataFlowType();
        return expression;
    }

    public visitMetaVariableRef(context: any): Expression {
        const expression = super.visitMetaVariableRef(context);
        const column = this._findColumn(expression.metaVariable.name);
        if (column) {
            // TODO - we should use cloneType here, when we start supporting parameters with array/complex type
            expression.returnType = DataFlowTypesUtils.cloneType(column.dataFlowColumn.dataFlowColumnType);
        } else {
            this._addError(expression.metaVariable, stringResources.DataFlowEditorErrorParameterNotFound);
        }

        this._validators.forEach((validator) => validator.visitMetaVariableRef(expression.metaVariable));

        return expression;
    }

    public visitMetaPosRef(context: any): Expression {
        const expression = super.visitMetaPosRef(context);
        const column = this._findColumn(expression.metaVariable.name);
        if (column) {
            expression.returnType = DataFlowTypesUtils.cloneType(column.dataFlowColumn.dataFlowColumnType);
        } else {
            this._addError(expression.metaVariable, stringResources.DataFlowEditorErrorParameterNotFound);
        }

        this._validators.forEach((validator) => validator.visitMetaVariableRef(expression.metaVariable));

        return expression;
    }

    public visitMetaMatchRef(context: any): Expression {
        const expression = super.visitMetaMatchRef(context);
        const column = this._findColumn(expression.metaMatch.name);
        if (column) {
            // TODO - we should use cloneType here, when we start supporting parameters with array/complex type
            expression.returnType = DataFlowTypesUtils.cloneType(column.dataFlowColumn.dataFlowColumnType);
        } else {
            this._addError(expression.metaMatch, stringResources.DataFlowEditorErrorParameterNotFound);
        }

        this._validators.forEach((validator) => validator.visitMetaMatchRef(expression.metaMatch));

        return expression;
    }

    public visitMetaArgumentRef(context: any): Expression {
        const expression = super.visitMetaArgumentRef(context);
        const array = this.visit(expression.metaArgument.arrayOrMapReference);
        if (!array || !array.returnType) {
            this._addError(expression.metaArgument, stringResources.DataFlowEditorErrorUnableToParse_Generic);
        } else {
            // Special case for accumulator reference in reduce function, which does not take the array type but instead takes the accumulator type
            if (expression.value && (expression.value as MetaArgument).name === '#acc') {
                expression.returnType = DataFlowTypesUtils.cloneType(array.returnType);
            } else if (array.returnType instanceof MapDataFlowType &&
                expression.value && (expression.value as MetaArgument).name === '#key') {
                expression.returnType = DataFlowTypesUtils.cloneType((array.returnType as MapDataFlowType).mapKeyType);
            } else if (array.returnType instanceof MapDataFlowType &&
                expression.value && (expression.value as MetaArgument).name === '#value') {
                expression.returnType = DataFlowTypesUtils.cloneType((array.returnType as MapDataFlowType).mapValueType);
            } else {
                expression.returnType = array.returnType.arrayType ? DataFlowTypesUtils.cloneType(array.returnType.arrayType) : new AnyDataFlowType();
            }
        }
        return expression;
    }

    public visitComparison(context: any): Expression {
        const expression = super.visitComparison(context);
        const leftExpression = expression.comparison.leftExpression;
        const rightExpression = expression.comparison.rightExpression;
        if (leftExpression && rightExpression) {
            const expressions: Expression[] = [leftExpression, rightExpression];
            const errors: IErrorExpression[] = DataFlowExpressionFunctions.VALIDATE_COMPARE_TYPE_ARGS(expressions);
            if (errors) {
                errors.forEach((error) => this._addError(error.expression, error.errorMessage));
            }
        }
        expression.returnType = new BooleanDataFlowType();
        this._validators.forEach((validator) => validator.visitComparison(expression));
        return expression;
    }

    public visitScope(context: any): Expression {
        const expression = super.visitScope(context);
        if (expression.scope.expression) {
            expression.returnType = expression.scope.expression.returnType;
        }
        this._validators.forEach((validator) => validator.visitScope(expression));
        return expression;
    }

    public visitEqNotEq(context: any): Expression {
        const expression = super.visitEqNotEq(context);
        // EQUALS_IGNORE_CASE only supports string type comparison.
        const leftExpression = expression.eqNotEq.leftExpression;
        const rightExpression = expression.eqNotEq.rightExpression;
        if (context.EQ_NOCASE()) {
            this._checkExpressionStringType(leftExpression);
            this._checkExpressionStringType(rightExpression);
        } else {
            if (leftExpression && rightExpression) {
                const expressions: Expression[] = [leftExpression, rightExpression];
                const errors: IErrorExpression[] = DataFlowExpressionFunctions.VALIDATE_EQUALITY_TYPE_ARGS(expressions);
                if (errors) {
                    errors.forEach((error) => this._addError(error.expression, error.errorMessage));
                }
            }
        }

        expression.returnType = new BooleanDataFlowType();
        this._validators.forEach((validator) => validator.visitEqNotEq(expression));
        return expression;
    }

    public visitMulDivMod(context: any): Expression {
        const expression = super.visitMulDivMod(context);
        const leftExpression = expression.mulDivMod.leftExpression;
        const rightExpression = expression.mulDivMod.rightExpression;
        if (leftExpression && rightExpression) {
            const expressions: Expression[] = [leftExpression, rightExpression];
            const errors: IErrorExpression[] = DataFlowExpressionFunctions.VALIDATE_MUL_DIV_MOD_ARGS(expressions);
            if (errors) {
                errors.forEach((error) => this._addError(error.expression, error.errorMessage));
            }
        }
        expression.returnType = this._getReturnTypeForBinaryOperator(leftExpression, rightExpression, expression.mulDivMod);
        this._validators.forEach((validator) => validator.visitMulDivMod(expression));
        return expression;
    }

    public visitMetaFunctionExpr(context: any): Expression {
        const expression = super.visitMetaFunctionExpr(context);
        const dataflowFunction = this._findFunction(expression.metafunction.basicName.identifier.name);
        if (dataflowFunction) {
            expression.returnType = DataFlowTypesUtils.createType(dataflowFunction.type as DataFlowDataType);
            expression.metafunction.children.forEach((child, index: number) => {
                if (child instanceof Expression) {
                    this._validateColumnType(child, dataflowFunction, index);
                } else if (child instanceof Assignment || child instanceof MetaAssignment) {
                    this._addError(child, stringResources.DataFlowEditorErrorUnableToAssignValues);
                } else {
                    this._addError(child, stringResources.DataFlowEditorErrorUnableToParse_Generic);
                }
            });
            this._checkTotalNumberOfArguments(dataflowFunction, expression.metafunction);
            this._validators.forEach((validator) => validator.visitMetaFunctionExpr(expression));
        } else {
            this._addError(expression.metafunction.basicName, stringResources.DataFlowEditorErrorUnknownFunction);
        }

        return expression;
    }

    public visitTransformFunctionExpr(context: any): Expression {
        const expression = super.visitTransformFunctionExpr(context);
        const functionId = expression.transformFunction.basicName.identifier;
        const dataFlowFunction = this._findFunction(functionId && functionId.name);
        const dataflowCacheSink = this._findCacheSink(expression.transformFunction.transformName);
        if (dataFlowFunction && dataflowCacheSink) {
            const cacheTypeExpression = new Expression();
            // generating cache type in here in case of updates
            const cacheSchemaType = new ComplexDataFlowType();
            dataflowCacheSink.cacheSink.outputColumns().forEach((column) => {
                const dataFlowExpression = new DataFlowExpression();
                dataFlowExpression.returnType = DataFlowTypesUtils.cloneType(column.dataFlowColumnType);
                cacheSchemaType.fields.push(new ComplexField(column.name, dataFlowExpression, cacheSchemaType));
            });
            const cacheType = new ArrayDataFlowType();
            cacheType.arrayType = cacheSchemaType;
            cacheTypeExpression.returnType = cacheType as ArrayDataFlowType;
            this.usedIdentifiers.dataFlowFunctions.push({
                expression: [expression],
                dataFlowFunction
            });
            this.usedIdentifiers.dataFlowCacheSinks.push({
                expression: [expression],
                cacheSink: dataflowCacheSink
            });
            expression.returnType = dataFlowFunction.evaluateType([cacheTypeExpression]);
            expression.transformFunction.children.forEach((child, index: number) => {
                this._validateColumnType(child as Expression, dataFlowFunction, index);
            });
            this._checkTotalNumberOfArguments(dataFlowFunction, expression.transformFunction);
            const errors = dataFlowFunction.validate(expression, context.parentCtx, dataflowCacheSink.cacheSink);
            if (errors) {
                errors.forEach((errorExpression) => this._addError(errorExpression.expression || expression.transformFunction.basicName, errorExpression.errorMessage));
            }
            this._validators.forEach((validator) => validator.visitFunctionExpr(expression));
        } else {
            this._addError(expression.transformFunction.basicName, stringResources.DataFlowEditorErrorUnknownFunction);
        }

        return expression;
    }

    public visitUnaryMinus(context: any): Expression {
        const expression = super.visitUnaryMinus(context);
        this._checkExpressionNumberType(expression.unaryMinus.expression);

        if (expression.unaryMinus.expression) {
            expression.returnType = expression.unaryMinus.expression.returnType;
        }

        this._validators.forEach((validator) => validator.visitUnaryMinus(expression));

        return expression;
    }

    public visitNot(context: any): Expression {
        const expression = super.visitNot(context);
        this._checkExpressionBooleanType(expression.not.expression);
        expression.returnType = new BooleanDataFlowType();

        this._validators.forEach((validator) => validator.visitNot(expression));

        return expression;
    }

    private _addError(expression: BasicExpression, message: string, treatErrorAsWarning: boolean = false): void {
        this._addErrorBase(expression.startPosition.line,
            expression.startPosition.column,
            expression.stopPosition.line,
            expression.stopPosition.column, message,
            treatErrorAsWarning);
    }

    private _addErrorBase(startLine: number, startColumn: number, endLine: number, endColumn: number, message: string, treatErrorAsWarning: boolean = false): void {
        if (!treatErrorAsWarning) {
            this.expressionErrors.push(message);
        }

        const sev: MarkerSeverity = treatErrorAsWarning ? MarkerSeverity.Warning : MarkerSeverity.Error;
        this.modelMarkers.push({
            severity: sev,
            message,
            startLineNumber: startLine,
            startColumn: startColumn + 1,
            endLineNumber: endLine,
            endColumn: endColumn + 2
        });
    }

    private _validateAddMinusArgType(leftExpression: Expression, rightExpression: Expression, isMinus: boolean): void {
        const expressions: Expression[] = [leftExpression, rightExpression];
        const errors: IErrorExpression[] = DataFlowExpressionFunctions.VALIDATE_ADD_MINUS_ARGS(expressions, isMinus);
        if (errors) {
            errors.forEach((error) => this._addError(error.expression, error.errorMessage));
        }
    }

    private _getReturnTypeForBinaryOperator(leftExpression: Expression, rightExpression: Expression, operator: BinaryOperator): IDataFlowDataType {
        if (!leftExpression || !rightExpression || !leftExpression.returnType || !rightExpression.returnType) {
            return undefined;
        }

        let returnType: IDataFlowDataType;
        if (operator instanceof AddMinus) {
            const expressions = [leftExpression, rightExpression];
            returnType = operator.operation === 'plus' ? DataFlowExpressionFunctions.MATH_ADD_TYPE_HANDLER(expressions) : DataFlowExpressionFunctions.MATH_MINUS_TYPE_HANDLER(expressions);
        }

        return returnType || DataFlowTypesUtils.getWiderType(leftExpression.returnType, rightExpression.returnType);
    }

    private _findParameter(paramenterName: string): DataFlowParameterArgument {
        return this._availableDataFlowArguments.dataFlowParameters.find((value) => value.name === paramenterName);
    }

    private _findColumn(columnName: string): DataFlowColumnArgument {
        return this._availableDataFlowArguments.dataFlowColumns.find((value) => value.name === columnName);
    }

    private _findTransientColumn(columnName: string): DataFlowColumnArgument {
        return this._availableDataFlowArguments.dataFlowTransientColumns.find((value) => value.name === columnName);
    }

    private _findFunction(functionName: string): DataFlowFunctionArgument {
        return this._availableDataFlowArguments.dataFlowFunctions.find((value) => value.name === functionName || value.synonyms.includes(functionName));
    }

    private _findUserDefinedFunction(functionName: string): DataFlowFunctionArgument {
        // eslint-disable-next-line no-restricted-syntax
        return Application.injector.get<UserDefinedFunctionsService>(UserDefinedFunctionsService).getUserDefinedFunctionsForExpressionBuilder()?.find((func) => func.name === functionName);
    }

    private _findCacheSink(cacheSinkName: string): DataFlowCacheSinkArgument {
        return this._availableDataFlowArguments.dataFlowCacheSinks.find((value) => value.name === cacheSinkName);
    }

    private _findUserDefinedArgument(argumentName: string): DataFlowUserDefinedArgument {
        return this._availableDataFlowArguments.dataFlowUserDefinedArguments && this._availableDataFlowArguments.dataFlowUserDefinedArguments.find((value) => value.name === argumentName);
    }

    private _validateColumnType(expression: Expression, dataflowFunction: DataFlowFunctionArgument, index: number): void {
        if (!dataflowFunction.variableArguments && index >= dataflowFunction.maximumNumberOfArguments) {
            this._addError(expression, format(stringResources.DataFlowEditorErrorWrongNumberOfMaximumParameters,
                dataflowFunction.name, `${dataflowFunction.maximumNumberOfArguments}`));
        } else {
            if (expression.returnType instanceof NullDataFlowType && dataflowFunction.type === 'any') {
                return;
            }
            const argumentType = dataflowFunction.inputTypes[
                dataflowFunction.variableArguments
                    ? index < dataflowFunction.maximumNumberOfArguments ? index : dataflowFunction.maximumNumberOfArguments - 1
                    : index];
            // TODO: nimoolen properly handle unary/binary function arguments
            if (argumentType !== 'any' && argumentType !== 'unaryfunction' && argumentType !== 'binaryfunction' && expression.returnType
                && expression.returnType.typeString !== 'any'
                && !DataFlowTypesUtils.typeMatch(expression.returnType.typeString, argumentType)) {
                this._addError(expression, format(stringResources.DataFlowEditorErrorWrongTypeParameter,
                    dataflowFunction.name, dataflowFunction.inputTypes[index]));
            }
        }
    }

    private _checkTotalNumberOfArguments(dataflowFunction: DataFlowFunctionArgument, expression: FunctionExpression | MetaFunction): void {
        if (!dataflowFunction.variableArguments) {
            if (expression.children.length < dataflowFunction.minimumRequiredNumberOfArguments) {
                this._addError(expression, format(stringResources.DataFlowEditorErrorWrongNumberOfMinimumParameters,
                    dataflowFunction.name, `${dataflowFunction.minimumRequiredNumberOfArguments}`));
            } else if (expression.children.length > dataflowFunction.maximumNumberOfArguments) {
                this._addError(expression, format(stringResources.DataFlowEditorErrorWrongNumberOfMaximumParameters,
                    dataflowFunction.name, `${dataflowFunction.maximumNumberOfArguments}`));
            }
        }
    }

    private _checkExpressionBooleanType(expression: Expression): void {
        if (!!expression && !!expression.returnType && !DataFlowTypesUtils.isBooleanType(expression.returnType)) {
            this._addError(expression, format(stringResources.DataFlowEditorErrorExpression, BooleanDataFlowType.TYPE));
        }
    }

    private _checkExpressionNumberType(expression: Expression): void {
        if (!!expression && !!expression.returnType && !DataFlowTypesUtils.isNumberType(expression.returnType)) {
            this._addError(expression, format(stringResources.DataFlowEditorErrorExpression, NumberDataFlowType.TYPE));
        }
    }

    private _checkExpressionStringType(expression: Expression): void {
        if (!!expression && !!expression.returnType && !DataFlowTypesUtils.isStringType(expression.returnType)) {
            this._addError(expression, format(stringResources.DataFlowEditorErrorExpression, StringDataFlowType.TYPE));
        }
    }

    private _convertDataType(columnType: Datatype): IDataFlowDataType {
        if (!columnType) {
            return new AnyDataFlowType();
        }
        let dataType: IDataFlowDataType;
        if (columnType.datatype === 'decimal') {
            dataType = DataFlowTypesUtils.createType(DecimalDataFlowType.TYPE);
            (dataType as DecimalDataFlowType).precision = (columnType as DecimalDatatype).precision;
            (dataType as DecimalDataFlowType).scale = (columnType as DecimalDatatype).scale;
        } else if (columnType.datatype === 'complex') {
            dataType = DataFlowTypesUtils.createType(ComplexDataFlowType.TYPE);
            (columnType as ComplexDatatype).columnDefinitions.forEach((columnDefinition) => {
                const nestedField: IDataFlowDataType = this._convertDataType(columnDefinition.datatype);
                const dataFlowExpression = new DataFlowExpression();
                dataFlowExpression.expression = columnDefinition.datatype.typestring;
                dataFlowExpression.returnType = nestedField;
                (dataType as ComplexDataFlowType).fields.push(new ComplexField(columnDefinition.name.name, dataFlowExpression, dataType as ComplexDataFlowType));
            });
        } else if (columnType.datatype === 'map') {
            const mapDataType = columnType as MapDatatype;
            dataType = DataFlowTypesUtils.createType(MapDataFlowType.TYPE,
                this._convertDataType(mapDataType.keyType).fullyQualifiedType, this._convertDataType(mapDataType.valueType).fullyQualifiedType);
        } else if (columnType.datatype === 'array') {
            dataType = DataFlowTypesUtils.createType(ArrayDataFlowType.TYPE);
            (dataType as ArrayDataFlowType).arrayType = this._convertDataType((columnType as ArrayDatatype).arrayType);
        } else if (columnType.datatype === 'external') {
            const externalTypeColumn = columnType as ExternalDataType;
            dataType = DataFlowTypesUtils.createType(ExternalDataFlowType.TYPE);
            const dataFlowExpression = new DataFlowExpression();
            dataFlowExpression.expression = externalTypeColumn.expression ? externalTypeColumn.expression.text : '';
            dataFlowExpression.returnType = externalTypeColumn.expression ? externalTypeColumn.expression.returnType : new AnyDataFlowType();
            (dataType as ExternalDataFlowType).expression = dataFlowExpression;
        } else {
            dataType = DataFlowTypesUtils.createType(columnType.datatype);
        }

        return dataType;
    }
}
